import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Loader2, Star, CheckCircle, Crown, Instagram, MapPin, Mail, Lock, Phone,
  Camera, X, Sparkles, BadgeCheck, TrendingUp, Users, Play, Zap, Gift, ArrowRight
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuickSignup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [prefillData, setPrefillData] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Welcome, 2: Upload, 3: Account, 4: Payment
  const [authToken, setAuthToken] = useState(null);
  const [userId, setUserId] = useState(null);
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: ""
  });
  
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  useEffect(() => {
    if (token) {
      fetchPrefillData();
    } else {
      setError("Invalid signup link");
      setLoading(false);
    }
    
    // Load Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [token]);

  const fetchPrefillData = async () => {
    try {
      const res = await axios.get(`${API}/quick-signup/${token}`);
      setPrefillData(res.data);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired signup link");
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    if (uploadedFiles.length + files.length > 6) {
      toast.error("Maximum 6 files allowed");
      return;
    }
    
    setUploading(true);
    const newFiles = [];
    
    for (const file of files) {
      const isVideo = file.type.startsWith('video/');
      const maxSize = isVideo ? 100 * 1024 * 1024 : 50 * 1024 * 1024;
      
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large`);
        continue;
      }
      
      const preview = URL.createObjectURL(file);
      newFiles.push({
        file,
        preview,
        type: isVideo ? 'video' : 'image',
        name: file.name
      });
    }
    
    setUploadedFiles([...uploadedFiles, ...newFiles]);
    setUploading(false);
    e.target.value = '';
  };

  const removeFile = (index) => {
    const newFiles = [...uploadedFiles];
    URL.revokeObjectURL(newFiles[index].preview);
    newFiles.splice(index, 1);
    setUploadedFiles(newFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await axios.post(`${API}/quick-signup/${token}/complete`, {
        email: formData.email,
        password: formData.password
      });
      
      const newAuthToken = res.data.token;
      setAuthToken(newAuthToken);
      setUserId(res.data.user_id);
      
      localStorage.setItem("token", newAuthToken);
      localStorage.setItem("authToken", newAuthToken);
      localStorage.setItem("user", JSON.stringify({
        user_id: res.data.user_id,
        user_type: res.data.user_type
      }));
      
      // Upload files if any
      if (uploadedFiles.length > 0) {
        toast.loading("Uploading your portfolio...", { id: "upload" });
        
        const uploadedUrls = [];
        for (const item of uploadedFiles) {
          try {
            const fd = new FormData();
            fd.append("file", item.file);
            
            const uploadRes = await axios.post(`${API}/upload/talent-photo`, fd, {
              headers: {
                'Authorization': `Bearer ${newAuthToken}`,
                'Content-Type': 'multipart/form-data'
              },
              timeout: 120000
            });
            
            uploadedUrls.push(uploadRes.data.file_id);
          } catch (err) {
            console.error("Upload failed:", err);
          }
        }
        
        if (uploadedUrls.length > 0) {
          await axios.put(`${API}/talent/profile`, {
            photos: uploadedUrls
          }, {
            headers: { 'Authorization': `Bearer ${newAuthToken}` }
          });
        }
        
        toast.dismiss("upload");
      }
      
      toast.success("🎉 Profile activated!");
      setStep(4); // Go to payment step
      
    } catch (err) {
      toast.error(err.response?.data?.detail || "Signup failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleProPayment = async () => {
    if (!authToken) {
      toast.error("Please complete signup first");
      return;
    }
    
    setProcessingPayment(true);
    
    try {
      const res = await axios.post(`${API}/talent/purchase-verified-badge`, {}, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      const options = {
        key: res.data.key_id,
        amount: res.data.amount_paise,
        currency: res.data.currency || "INR",
        name: "FOUNDSTAR",
        description: "Pro Creator Badge",
        order_id: res.data.order_id,
        handler: async (paymentResponse) => {
          try {
            await axios.post(`${API}/talent/verify-badge-payment`, {
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature: paymentResponse.razorpay_signature
            }, { headers: { 'Authorization': `Bearer ${authToken}` } });
            
            toast.success("🎉 You're now a Pro Creator!");
            setTimeout(() => navigate("/influencer/profile"), 2000);
          } catch (error) {
            toast.error("Payment verification failed");
            setProcessingPayment(false);
          }
        },
        modal: {
          ondismiss: () => setProcessingPayment(false)
        },
        prefill: {
          email: formData.email,
          name: prefillData?.name || ""
        },
        theme: { color: "#7B2FBE" }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate payment");
      setProcessingPayment(false);
    }
  };

  const skipPayment = () => {
    toast.success("Welcome to FOUNDSTAR!");
    navigate("/influencer/profile");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f0f23] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 flex items-center justify-center animate-pulse">
            <Star className="w-8 h-8 text-white" />
          </div>
          <p className="text-white/60">Loading your profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f0f23] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white/5 backdrop-blur-xl rounded-3xl p-8 text-center border border-white/10">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <X className="w-10 h-10 text-red-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Link Expired</h2>
          <p className="text-white/60 mb-6">{error}</p>
          <Button onClick={() => navigate("/")} className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700">
            Visit FOUNDSTAR
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f0f23] overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pink-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <div className="p-4 flex items-center justify-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-r from-pink-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Star className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">FOUNDSTAR</span>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-lg">
            
            {/* Step 1: Welcome */}
            {step === 1 && (
              <div className="text-center animate-fadeIn">
                <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 border border-white/10 mb-6">
                  <div className="relative w-24 h-24 mx-auto mb-4">
                    <div className="w-24 h-24 rounded-full bg-gradient-to-br from-pink-500 via-purple-500 to-blue-500 p-1">
                      <div className="w-full h-full rounded-full bg-[#1a1a2e] flex items-center justify-center">
                        <span className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-400">
                          {prefillData?.name?.charAt(0) || "S"}
                        </span>
                      </div>
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center border-4 border-[#1a1a2e]">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  
                  <h1 className="text-2xl font-bold text-white mb-1">
                    Hey {prefillData?.name?.split(' ')[0]}! 👋
                  </h1>
                  <p className="text-white/60 mb-4">You're invited to join FOUNDSTAR</p>
                  
                  <div className="flex flex-wrap justify-center gap-2 mb-6">
                    <span className="px-3 py-1.5 bg-pink-500/20 text-pink-300 rounded-full text-sm flex items-center gap-1">
                      <BadgeCheck className="w-3.5 h-3.5" />
                      {prefillData?.category?.replace('_', ' ')}
                    </span>
                    {prefillData?.city && (
                      <span className="px-3 py-1.5 bg-purple-500/20 text-purple-300 rounded-full text-sm flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {prefillData.city}
                      </span>
                    )}
                    {prefillData?.instagram_handle && (
                      <span className="px-3 py-1.5 bg-blue-500/20 text-blue-300 rounded-full text-sm flex items-center gap-1">
                        <Instagram className="w-3.5 h-3.5" />
                        @{prefillData.instagram_handle}
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
                    <Users className="w-6 h-6 text-pink-400 mx-auto mb-2" />
                    <p className="text-white font-semibold text-lg">500+</p>
                    <p className="text-white/40 text-xs">Brands</p>
                  </div>
                  <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
                    <TrendingUp className="w-6 h-6 text-purple-400 mx-auto mb-2" />
                    <p className="text-white font-semibold text-lg">3x</p>
                    <p className="text-white/40 text-xs">More Deals</p>
                  </div>
                  <div className="bg-white/5 backdrop-blur rounded-2xl p-4 border border-white/10">
                    <Crown className="w-6 h-6 text-yellow-400 mx-auto mb-2" />
                    <p className="text-white font-semibold text-lg">Pro</p>
                    <p className="text-white/40 text-xs">Status</p>
                  </div>
                </div>

                <Button
                  onClick={() => setStep(2)}
                  className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 rounded-2xl shadow-lg shadow-pink-500/25"
                >
                  Let's Create Your Profile
                  <Sparkles className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}

            {/* Step 2: Upload */}
            {step === 2 && (
              <div className="animate-fadeIn">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-white mb-2">Show Your Best Work ✨</h2>
                  <p className="text-white/60">Upload photos or videos to attract brands</p>
                </div>

                <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 border border-white/10 mb-6">
                  <label className="block cursor-pointer">
                    <div className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
                      uploading ? 'border-pink-500 bg-pink-500/10' : 'border-white/20 hover:border-pink-500/50 hover:bg-white/5'
                    }`}>
                      <input
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        className="hidden"
                        onChange={handleFileUpload}
                        disabled={uploading}
                      />
                      {uploading ? (
                        <Loader2 className="w-12 h-12 text-pink-400 mx-auto mb-3 animate-spin" />
                      ) : (
                        <div className="w-16 h-16 bg-gradient-to-r from-pink-500/20 to-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                          <Camera className="w-8 h-8 text-pink-400" />
                        </div>
                      )}
                      <p className="text-white font-medium mb-1">
                        {uploading ? "Processing..." : "Tap to upload"}
                      </p>
                      <p className="text-white/40 text-sm">Photos & Videos (max 6)</p>
                    </div>
                  </label>

                  {uploadedFiles.length > 0 && (
                    <div className="grid grid-cols-3 gap-3 mt-4">
                      {uploadedFiles.map((item, index) => (
                        <div key={index} className="relative aspect-square rounded-xl overflow-hidden group">
                          {item.type === 'video' ? (
                            <div className="w-full h-full bg-black flex items-center justify-center">
                              <video src={item.preview} className="w-full h-full object-cover" />
                              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                                <Play className="w-8 h-8 text-white" />
                              </div>
                            </div>
                          ) : (
                            <img src={item.preview} alt="" className="w-full h-full object-cover" />
                          )}
                          <button
                            onClick={() => removeFile(index)}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4 text-white" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep(3)}
                    className="flex-1 py-6 border-white/20 text-white hover:bg-white/10 rounded-2xl"
                  >
                    Skip for now
                  </Button>
                  <Button
                    onClick={() => setStep(3)}
                    className="flex-1 py-6 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 rounded-2xl"
                  >
                    Continue {uploadedFiles.length > 0 && `(${uploadedFiles.length})`}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Account */}
            {step === 3 && (
              <div className="animate-fadeIn">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-white mb-2">Almost Done! 🎉</h2>
                  <p className="text-white/60">Create your login credentials</p>
                </div>

                <form onSubmit={handleSubmit} className="bg-white/5 backdrop-blur-xl rounded-3xl p-6 border border-white/10">
                  <div className="flex items-center gap-3 p-3 bg-white/5 rounded-2xl mb-6">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg">
                      {prefillData?.name?.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{prefillData?.name}</p>
                      <p className="text-white/40 text-sm">+91 {prefillData?.phone}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-white/60 text-sm">Email Address</Label>
                      <div className="relative mt-1">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <Input
                          type="email"
                          required
                          placeholder="your@email.com"
                          className="pl-12 py-6 bg-white/5 border-white/10 text-white rounded-xl placeholder:text-white/30"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-white/60 text-sm">Create Password</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <Input
                          type="password"
                          required
                          placeholder="Min 6 characters"
                          className="pl-12 py-6 bg-white/5 border-white/10 text-white rounded-xl placeholder:text-white/30"
                          value={formData.password}
                          onChange={(e) => setFormData({...formData, password: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-white/60 text-sm">Confirm Password</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                        <Input
                          type="password"
                          required
                          placeholder="Confirm password"
                          className="pl-12 py-6 bg-white/5 border-white/10 text-white rounded-xl placeholder:text-white/30"
                          value={formData.confirmPassword}
                          onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting}
                    className="w-full mt-6 py-6 text-lg font-semibold bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 rounded-2xl"
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Creating...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5" />
                        Create My Profile
                      </span>
                    )}
                  </Button>

                  <button type="button" onClick={() => setStep(2)} className="w-full mt-3 py-3 text-white/50 hover:text-white text-sm">
                    ← Back
                  </button>
                </form>
              </div>
            )}

            {/* Step 4: Payment */}
            {step === 4 && (
              <div className="animate-fadeIn">
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">Profile Created! 🎉</h2>
                  <p className="text-white/60">One more step to unlock all benefits</p>
                </div>

                {/* Pro Creator Card */}
                <div className="bg-gradient-to-br from-yellow-500/20 via-orange-500/20 to-pink-500/20 backdrop-blur-xl rounded-3xl p-6 border border-yellow-500/30 mb-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-14 h-14 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center">
                      <Crown className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">Become Pro Creator</h3>
                      <p className="text-yellow-200/80 text-sm">Unlock premium features</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center gap-3 text-white/90">
                      <Zap className="w-5 h-5 text-yellow-400" />
                      <span>Get featured to 500+ brands</span>
                    </div>
                    <div className="flex items-center gap-3 text-white/90">
                      <BadgeCheck className="w-5 h-5 text-yellow-400" />
                      <span>Pro Creator verified badge</span>
                    </div>
                    <div className="flex items-center gap-3 text-white/90">
                      <TrendingUp className="w-5 h-5 text-yellow-400" />
                      <span>3x more brand deal requests</span>
                    </div>
                    <div className="flex items-center gap-3 text-white/90">
                      <Gift className="w-5 h-5 text-yellow-400" />
                      <span>Select multiple niche categories</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <span className="text-3xl font-bold text-white">₹199</span>
                      <span className="text-white/50 text-sm ml-2 line-through">₹499</span>
                    </div>
                    <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm font-medium">
                      60% OFF
                    </span>
                  </div>

                  {/* Highlight CTA */}
                  <div className="mb-4 p-3 bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30 rounded-xl">
                    <p className="text-center text-white font-semibold flex items-center justify-center gap-2">
                      <Sparkles className="w-4 h-4 text-pink-400" />
                      Join Pro to claim your first brand deal now!
                      <Sparkles className="w-4 h-4 text-pink-400" />
                    </p>
                  </div>

                  <Button
                    onClick={handleProPayment}
                    disabled={processingPayment}
                    className="w-full py-6 text-lg font-semibold bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-2xl shadow-lg shadow-orange-500/25"
                  >
                    {processingPayment ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Crown className="w-5 h-5" />
                        Pay ₹199 & Go Pro
                        <ArrowRight className="w-5 h-5" />
                      </span>
                    )}
                  </Button>
                </div>

                {/* Skip Option */}
                <button
                  onClick={skipPayment}
                  className="w-full py-4 text-white/50 hover:text-white/80 text-sm transition-colors"
                >
                  Skip for now, I'll upgrade later →
                </button>

                <p className="text-center text-white/30 text-xs mt-4">
                  🔒 Secure payment powered by Razorpay
                </p>
              </div>
            )}

            {/* Step Indicator */}
            <div className="flex justify-center gap-2 mt-6">
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  className={`h-1.5 rounded-full transition-all ${
                    s === step ? 'w-8 bg-gradient-to-r from-pink-500 to-purple-600' : 
                    s < step ? 'w-4 bg-pink-500/50' : 'w-4 bg-white/20'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }
      `}</style>
    </div>
  );
}
